/**
 * JAISIU-4437 — seekable progress bar for video overlays.
 *
 * ## Why this exists
 *
 * The attachment preview overlay renders a native
 * `<video controls>` element for `video` attachments
 * (`attachment-preview-overlay.ts::renderVideoBody`). The browser's own
 * scrubber is part of the native control chrome, but:
 *
 *   - it is only visible while the controls are shown (they auto-hide
 *     on idle), so the operator has to hover the element to get it back;
 *   - on the overlay's scaled backdrop it is a thin sliver, and in the
 *     collapsed / small-card contexts the overlay renders it at a size
 *     where a precise seek is not possible;
 *   - the native scrubber is unreachable for keyboard users once the
 *     custom overlay chrome takes focus.
 *
 * So the overlay gets its OWN progress bar: an explicit
 * `role="slider"` track that is always visible, always keyboard
 * reachable, and that seeks on click AND on drag.
 *
 * ## Seeking semantics
 *
 * The bar is driven entirely by the `<video>` element's own state:
 *
 *   - `video.duration` — the total length of the clip.
 *   - `video.currentTime` — the playhead position.
 *
 * A pointer position on the track maps to a time via the track's
 * bounding rect:
 *
 *     ratio  = (clientX - rect.left) / rect.width      (clamped 0..1)
 *     target = ratio * duration
 *
 * Clicking seeks immediately. Dragging seeks continuously while the
 * pointer is down (pointer capture, so the drag survives the pointer
 * leaving the track). We deliberately do NOT try to replicate
 * "scrub-preview" behaviour (playing while dragging) — that fights the
 * browser's own playback and makes short clips stutter.
 *
 * ## Interaction contract
 *
 *   - `pointerdown` on the track → seek + begin drag.
 *   - `pointermove` while dragging → seek.
 *   - `pointerup` / `pointercancel` → end drag.
 *   - `keydown` on the track → ArrowLeft / ArrowRight nudge by
 *     `step` seconds, Home / End jump to the ends, PageUp / PageDown
 *     nudge by a larger step. This mirrors the native media element's
 *     keyboard contract.
 *
 * ## Not in scope
 *
 * This module owns the SEEKING UI only. Whether the bytes for the
 * requested range are actually delivered is a gateway concern (HTTP
 * `Range` / `206 Partial Content` support in
 * `src/gateway/session-files/session-files.route.ts`) and is tracked
 * separately. The bar still reflects `video.currentTime`, so if the
 * gateway cannot serve the range the browser clamps the seek itself and
 * the bar stays consistent with what is actually playing.
 */

import { html, type TemplateResult } from "lit";

/**
 * Smallest / largest fraction of the clip a single arrow-key nudge
 * moves. Expressed in seconds so the behaviour is independent of the
 * clip length (a 4-second clip and a 4-minute clip both nudge by the
 * same wall-clock amount).
 */
export const VIDEO_PROGRESS_STEP_SECONDS = 5;

/** Larger nudge used by PageUp / PageDown. */
export const VIDEO_PROGRESS_LARGE_STEP_SECONDS = 30;

/**
 * Clamp a value into `[min, max]`. Non-finite input collapses to
 * `min` — a NaN duration must never leak into `video.currentTime`,
 * because assigning NaN to `currentTime` throws in the browser.
 */
export function clampNumber(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

/**
 * Map a pointer position to a playhead ratio in `[0, 1]`.
 *
 * `rect` is the track's `DOMRect` (or any `{ left, width }` shape).
 * A zero / negative width (element not laid out yet) yields `0`
 * rather than `NaN`.
 */
export function pointerRatio(
  clientX: number,
  rect: { left: number; width: number },
): number {
  if (!rect || !Number.isFinite(rect.width) || rect.width <= 0) return 0;
  if (!Number.isFinite(clientX)) return 0;
  return clampNumber((clientX - rect.left) / rect.width, 0, 1);
}

/**
 * Convert a pointer position on the track to the absolute time the
 * playhead should move to.
 *
 * Returns `0` for a clip whose duration is not yet known
 * (`duration` is `NaN` before `loadedmetadata`, `Infinity` for a
 * live stream) — seeking an unknown-length clip to an arbitrary
 * offset is meaningless.
 */
export function seekTimeFromPointer(
  clientX: number,
  rect: { left: number; width: number },
  duration: number,
): number {
  if (!Number.isFinite(duration) || duration <= 0) return 0;
  return pointerRatio(clientX, rect) * duration;
}

/**
 * Fraction of the clip already played, in `[0, 1]`. `0` when the
 * duration is unknown.
 */
export function playedRatio(currentTime: number, duration: number): number {
  if (!Number.isFinite(duration) || duration <= 0) return 0;
  if (!Number.isFinite(currentTime) || currentTime <= 0) return 0;
  return clampNumber(currentTime / duration, 0, 1);
}

/**
 * Format a number of seconds as `m:ss` (or `h:mm:ss` past an hour).
 * Used for the elapsed / total labels beside the track.
 */
export function formatMediaTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const total = Math.floor(seconds);
  const s = total % 60;
  const m = Math.floor(total / 60) % 60;
  const h = Math.floor(total / 3600);
  const pad = (n: number) => String(n).padStart(2, "0");
  if (h > 0) return `${h}:${pad(m)}:${pad(s)}`;
  return `${m}:${pad(s)}`;
}

/**
 * The subset of `HTMLMediaElement` the bar needs. Declared locally so
 * the module can be unit-tested with a plain object and does not force
 * a DOM lib on consumers.
 */
export interface VideoSeekTarget {
  duration: number;
  currentTime: number;
  volume?: number;
  muted?: boolean;
  paused?: boolean;
}

/**
 * A snapshot of the media state the bar renders.
 */
export interface VideoProgressState {
  /** Total clip length in seconds; `0` when unknown. */
  duration: number;
  /** Playhead position in seconds. */
  currentTime: number;
}

/**
 * Read the media state off a `<video>` (or `<audio>`) element,
 * normalising the `NaN` / `Infinity` values the browser reports
 * before metadata has loaded.
 */
export function readVideoProgressState(
  media: VideoSeekTarget | null | undefined,
): VideoProgressState {
  if (!media) return { duration: 0, currentTime: 0 };
  const duration = Number.isFinite(media.duration) ? media.duration : 0;
  const currentTime = Number.isFinite(media.currentTime)
    ? media.currentTime
    : 0;
  return { duration, currentTime };
}

/**
 * Seek the media element, defensively.
 *
 * Assigning `currentTime` on a media element whose metadata has not
 * loaded yet throws in some browsers (`InvalidStateError`), so we
 * guard both the duration check and the assignment.
 */
export function applySeek(
  media: (VideoSeekTarget & { currentTime: number }) | null | undefined,
  seconds: number,
): boolean {
  if (!media) return false;
  if (!Number.isFinite(seconds) || seconds < 0) return false;
  try {
    media.currentTime = seconds;
    return true;
  } catch {
    // Media element not ready yet (no metadata) — the caller will
    // retry on the next interaction.
    return false;
  }
}

/**
 * Compute the next playhead position for a keyboard nudge.
 *
 * `key` is the `KeyboardEvent.key`. Returns `null` when the key is
 * not a seek key so the caller can let the event bubble.
 */
export function keyboardSeekTarget(
  key: string,
  state: VideoProgressState,
  step = VIDEO_PROGRESS_STEP_SECONDS,
  largeStep = VIDEO_PROGRESS_LARGE_STEP_SECONDS,
): number | null {
  const duration = state.duration;
  const current = state.currentTime;
  // Without a known duration we cannot express "5 seconds from here"
  // as a fraction of the clip, and the clamp bounds are unknown, so
  // every positional nudge is a no-op. `Home` is still meaningful
  // (seek to the very start); `End` is not (we do not know where the
  // end is).
  const durationKnown = Number.isFinite(duration) && duration > 0;
  switch (key) {
    case "ArrowLeft":
    case "ArrowDown":
      if (!durationKnown) return null;
      return clampNumber(current - step, 0, duration);
    case "ArrowRight":
    case "ArrowUp":
      if (!durationKnown) return null;
      return clampNumber(current + step, 0, duration);
    case "PageDown":
      if (!durationKnown) return null;
      return clampNumber(current - largeStep, 0, duration);
    case "PageUp":
      if (!durationKnown) return null;
      return clampNumber(current + largeStep, 0, duration);
    case "Home":
      return 0;
    case "End":
      return durationKnown ? duration : null;
    default:
      return null;
  }
}


/**
 * The volume the player starts at when the media element does not
 * report a usable one. `1` is the browser default.
 */
export const VIDEO_DEFAULT_VOLUME = 1;

/**
 * Normalise a media element's volume into `[0, 1]`.
 *
 * The browser reports `NaN` before the element is configured and
 * clamps the assignment range to `[0, 1]`, so an out-of-range or
 * non-finite value collapses to the default rather than throwing.
 */
export function normalizeVolume(volume: number | undefined): number {
  if (volume === undefined || !Number.isFinite(volume)) {
    return VIDEO_DEFAULT_VOLUME;
  }
  return clampNumber(volume, 0, 1);
}

/**
 * Set the media element's volume, defensively.
 *
 * Returns the volume that was actually applied (the browser clamps
 * to `[0, 1]`).
 */
export function applyVolume(
  media: { volume?: number } | null | undefined,
  volume: number,
): number {
  if (!media) return VIDEO_DEFAULT_VOLUME;
  const next = normalizeVolume(volume);
  try {
    media.volume = next;
  } catch {
    return normalizeVolume(media.volume);
  }
  return normalizeVolume(media.volume);
}

/**
 * Toggle playback on a media element.
 *
 * Returns the resulting paused state (`true` when the media ended up
 * paused). A media element that cannot play (no source yet, decode
 * error) is left paused rather than throwing at the caller.
 */
export function togglePlayback(
  media: HTMLMediaElement | null | undefined,
): boolean {
  if (!media) return true;
  try {
    if (media.paused) {
      const played = media.play?.();
      // `play()` returns a promise that rejects when the media cannot
      // start; swallow it so the click handler never throws.
      if (played && typeof played.catch === "function") {
        played.catch(() => undefined);
      }
      return false;
    }
    media.pause?.();
    return true;
  } catch {
    return media.paused !== false;
  }
}

/**
 * Toggle the media element's fullscreen state.
 *
 * The request is made on the WRAPPER (the card), not on the `<video>`
 * element: entering fullscreen on the media element alone hides the
 * custom control bar (the native chrome would come with it, but the
 * native chrome is gone in JAISIU-4447), so the operator would lose
 * the only scrubber they have.
 *
 * Returns `true` when the element is in fullscreen after the call.
 */
export async function toggleFullscreen(
  element: HTMLElement | null | undefined,
): Promise<boolean> {
  if (!element) return false;
  const doc = element.ownerDocument ?? document;
  try {
    if (doc.fullscreenElement) {
      await doc.exitFullscreen?.();
      return false;
    }
    await element.requestFullscreen?.();
    return true;
  } catch {
    // Fullscreen can be blocked (no user gesture, permissions
    // policy, unsupported). The operator simply keeps the inline
    // player; no error is surfaced.
    return Boolean(doc.fullscreenElement);
  }
}

/**
 * Class name of the progress-bar track. The imperative updater and
 * the seek wiring both find the track through this class.
 */
export const VIDEO_PROGRESS_TRACK_CLASS =
  "attachment-preview-video-progress-track";

/**
 * Class name of the element that holds the whole bar (track + time
 * labels). The updater locates it relative to the `<video>` element.
 */
export const VIDEO_PROGRESS_ROOT_CLASS = "attachment-preview-video-progress";

/**
 * Seek helper used by the pointer handlers: maps the pointer's
 * position on the track to a time and hands it to `onSeek`.
 */
function seekVideoToPointer(
  track: HTMLElement,
  clientX: number,
  duration: number,
  onSeek: VideoSeekHandler,
): number {
  const rect = track.getBoundingClientRect();
  const target = seekTimeFromPointer(clientX, rect, duration);
  onSeek(target, track);
  return target;
}

/**
 * Seek handler invoked by the progress bar.
 *
 * The handler receives the absolute target time AND the track element
 * that produced the gesture. Passing the track lets the caller
 * resolve the media element relative to the bar (the bar and the
 * `<video>` share a wrapper), which keeps the wiring local — no
 * global `document.querySelector`, no cross-talk between overlays.
 */
export type VideoSeekHandler = (
  seconds: number,
  track: HTMLElement,
) => void;

/**
 * Resolver that returns the media element the bar drives.
 *
 * The bar reads the media element's LIVE `duration` / `currentTime`
 * on every gesture rather than a render-time snapshot. This matters:
 * the overlay template is rendered once when the preview opens, long
 * before the browser has loaded the clip's metadata, so a captured
 * snapshot would always read `duration === 0` and every seek would
 * land at the start of the clip.
 *
 * The resolver receives the TRACK element that produced the gesture,
 * so the caller can locate the media element relative to the bar
 * (they share a wrapper) instead of reaching into the whole
 * document.
 */
export type VideoMediaResolver = (
  track: HTMLElement,
) => VideoSeekTarget | null | undefined;

/**
 * Optional wiring for the bar's controls.
 *
 * `onSeekRequested` is called with the absolute target time every
 * time the operator asks for a new position (pointer press, drag
 * move, keyboard nudge). The overlay uses it to record the PENDING
 * seek so a stale `timeupdate` / `seeked` event cannot move the
 * playhead back before the browser lands the seek (JAISIU-4447).
 */
export interface VideoProgressBarOptions {
  /** Called with the requested seek target (seconds). */
  onSeekRequested?: (seconds: number) => void;
}

/**
 * Render the progress bar.
 *
 * The bar is presentational: it wires the pointer / keyboard
 * gestures and reads the live media state. The actual seeking is
 * delegated to `onSeek`, which the caller binds to the `<video>`
 * element.
 *
 * The rendered parts carry stable classes (not just `data-testid`s)
 * because the media-state updater ({@link updateVideoProgressBar})
 * mutates them directly on every `timeupdate` — re-rendering the
 * whole overlay on each tick would restart the `<video>` element.
 *
 * @param getMedia  Resolves the media element (live state source).
 * @param onSeek    Called with the absolute target time (seconds) on
 *                  click, drag, and keyboard nudge.
 * @param testid    Optional `data-testid` override for the track.
 * @param options   Optional wiring (pending-seek notification).
 */
export function renderVideoProgressBar(
  getMedia: VideoMediaResolver,
  onSeek: VideoSeekHandler,
  testid = "attachment-preview-video-progress",
  options: VideoProgressBarOptions = {},
): TemplateResult {
  // The initial paint uses whatever the media element reports right
  // now (usually "duration unknown"); the imperative updater keeps
  // it in step from then on. The track does not exist yet at this
  // point, so we resolve against the document root — the resolver
  // falls back gracefully.
  const initial = readVideoProgressState(getMedia(document.body));
  const ratio = playedRatio(initial.currentTime, initial.duration);
  const durationLabel =
    initial.duration > 0 ? formatMediaTime(initial.duration) : "--:--";
  const currentLabel = formatMediaTime(initial.currentTime);
  const volume = normalizeVolume(getMedia(document.body)?.volume);
  const paused = getMedia(document.body)?.paused !== false;

  return html`
    <div
      class="${VIDEO_PROGRESS_ROOT_CLASS} attachment-viewer-video-progress"
      data-testid=${testid}
    >
      <div
        class="${VIDEO_PROGRESS_TRACK_CLASS} attachment-viewer-video-progress-track"
        role="slider"
        tabindex="0"
        aria-label="Video position"
        data-testid="${testid}-track"
        aria-valuemin="0"
        aria-valuemax=${initial.duration > 0 ? initial.duration : 0}
        aria-valuenow=${initial.currentTime}
        aria-valuetext=${`${currentLabel} of ${durationLabel}`}
        data-duration=${initial.duration}
        data-current-time=${initial.currentTime}
        data-ratio=${ratio}
        @pointerdown=${(e: PointerEvent) => {
          if (e.button !== 0) return;
          e.preventDefault();
          e.stopPropagation();
          const track = e.currentTarget as HTMLElement;
          // Seek immediately on press so a plain click lands. The
          // duration is read LIVE — the clip's metadata may have
          // loaded since the template was rendered.
          const live = readVideoProgressState(getMedia(track));
          const target = seekVideoToPointer(
            track,
            e.clientX,
            live.duration,
            onSeek,
          );
          options.onSeekRequested?.(target);
          try {
            track.setPointerCapture(e.pointerId);
          } catch {
            // Pointer capture is best-effort; the drag still works
            // through the element-level listeners below.
          }
          const onMove = (ev: PointerEvent) => {
            const live = readVideoProgressState(getMedia(track));
            const target = seekVideoToPointer(
              track,
              ev.clientX,
              live.duration,
              onSeek,
            );
            options.onSeekRequested?.(target);
          };
          const onUp = () => {
            try {
              track.releasePointerCapture(e.pointerId);
            } catch {
              // Already released.
            }
            track.removeEventListener("pointermove", onMove as EventListener);
            track.removeEventListener("pointerup", onUp as EventListener);
            track.removeEventListener("pointercancel", onUp as EventListener);
          };
          track.addEventListener("pointermove", onMove as EventListener);
          track.addEventListener("pointerup", onUp as EventListener);
          track.addEventListener("pointercancel", onUp as EventListener);
        }}
        @keydown=${(e: KeyboardEvent) => {
          const live = readVideoProgressState(
            getMedia(e.currentTarget as HTMLElement),
          );
          const target = keyboardSeekTarget(e.key, live);
          if (target === null) return;
          e.preventDefault();
          e.stopPropagation();
          onSeek(target, e.currentTarget as HTMLElement);
          options.onSeekRequested?.(target);
        }}
      >
        <div
          class="attachment-preview-video-progress-fill attachment-viewer-video-progress-fill"
          style="width:${(ratio * 100).toFixed(2)}%;"
          data-testid="${testid}-fill"
        ></div>
      </div>
      <div
        class="attachment-preview-video-progress-times attachment-viewer-video-progress-times"
        data-testid="${testid}-times"
      >
        <span
          class="attachment-preview-video-progress-current attachment-viewer-video-progress-current"
          data-testid="${testid}-current"
          >${currentLabel}</span
        >
        <span
          class="attachment-preview-video-progress-duration attachment-viewer-video-progress-duration"
          data-testid="${testid}-duration"
          >${durationLabel}</span
        >
      </div>
      <div
        class="attachment-preview-video-progress-controls attachment-viewer-video-progress-controls"
        data-testid="${testid}-controls"
      >
        <button
          type="button"
          class="attachment-preview-video-progress-play attachment-viewer-video-progress-play"
          data-testid="${testid}-play"
          aria-label=${paused ? "Play" : "Pause"}
          title=${paused ? "Play" : "Pause"}
          data-paused=${paused ? "1" : "0"}
          @click=${(e: Event) => {
            e.preventDefault();
            e.stopPropagation();
            const btn = e.currentTarget as HTMLElement;
            const media = getMedia(btn) as HTMLMediaElement | null | undefined;
            if (!media) return;
            togglePlayback(media);
            updateVideoProgressBar(media);
          }}
        >
          ${paused ? "▶" : "❚❚"}
        </button>
        <label
          class="attachment-preview-video-progress-volume-label attachment-viewer-video-progress-volume-label"
          data-testid="${testid}-volume-label"
        >
          <span
            class="attachment-preview-video-progress-volume-icon"
            aria-hidden="true"
            >🔊</span
          >
          <input
            type="range"
            class="attachment-preview-video-progress-volume attachment-viewer-video-progress-volume"
            min="0"
            max="1"
            step="0.01"
            value=${volume}
            aria-label="Volume"
            data-testid="${testid}-volume"
            @input=${(e: Event) => {
              e.stopPropagation();
              const input = e.currentTarget as HTMLInputElement;
              const media = getMedia(input) as HTMLMediaElement | null | undefined;
              if (!media) return;
              const applied = applyVolume(media, Number(input.value));
              input.value = String(applied);
              updateVideoProgressBar(media);
            }}
          />
        </label>
        <button
          type="button"
          class="attachment-preview-video-progress-fullscreen attachment-viewer-video-progress-fullscreen"
          data-testid="${testid}-fullscreen"
          aria-label="Fullscreen"
          title="Fullscreen"
          @click=${(e: Event) => {
            e.preventDefault();
            e.stopPropagation();
            const btn = e.currentTarget as HTMLElement;
            const scope =
              (btn.closest(
                ".attachment-preview-video-wrap, .attachment-preview-media-wrap",
              ) as HTMLElement | null) ?? null;
            void toggleFullscreen(scope);
          }}
        >
          ⛶
        </button>
      </div>
    </div>
  `;
}

/**
 * Update the rendered progress bar to match the media element's
 * current state.
 *
 * The bar lives next to the `<video>` element inside the same
 * wrapper, so the updater finds it relative to the media element.
 * Returns `true` when the bar was found and updated.
 *
 * This is deliberately imperative rather than a Lit re-render: the
 * `<video>` element's state changes several times per second, and
 * re-rendering the overlay template on every `timeupdate` would
 * tear down and recreate the media element (which restarts playback
 * and loses the buffered range).
 *
 * ## Pending seeks (JAISIU-4447)
 *
 * `overrideCurrentTime` carries the position the operator REQUESTED
 * while a seek is still in flight. When present, the bar renders
 * that position instead of the media element's (possibly stale)
 * `currentTime`, so a `timeupdate` / `seeked` event that lands
 * before the browser finished the seek cannot snap the playhead
 * back. Once the media catches up, the caller drops the override
 * and the media position becomes authoritative again.
 */
export function updateVideoProgressBar(
  media: VideoSeekTarget | null | undefined,
  overrideCurrentTime?: number | null,
): boolean {
  if (!media) return false;
  const mediaEl = media as unknown as HTMLElement;
  if (typeof mediaEl.closest !== "function") return false;
  // The bar is a SIBLING of the media element (both live inside the
  // media wrapper), so we walk up to the wrapper and then down into
  // the bar. Looking for the bar as an ancestor of the media element
  // would never match.
  //
  // JAISIU-4443 — the overlay's video wrapper on this branch is
  // `attachment-preview-video-wrap`; the older
  // `attachment-preview-media-wrap` name is kept in the list so the
  // module stays forward-compatible if the wrapper is ever unified.
  const scope = (mediaEl.closest(
    ".attachment-preview-video-wrap, .attachment-preview-media-wrap",
  ) ?? mediaEl.parentElement) as HTMLElement | null;
  const host = (scope?.querySelector(
    `.${VIDEO_PROGRESS_ROOT_CLASS}`,
  ) ?? null) as HTMLElement | null;
  if (!host) return false;
  const track = host.querySelector(
    `.${VIDEO_PROGRESS_TRACK_CLASS}`,
  ) as HTMLElement | null;
  if (!track) return false;

  const state = readVideoProgressState(media);
  // A pending seek wins over the media's (possibly stale) position.
  const hasOverride =
    typeof overrideCurrentTime === "number" &&
    Number.isFinite(overrideCurrentTime);
  const displayedTime = hasOverride
    ? clampNumber(overrideCurrentTime as number, 0, Math.max(state.duration, 0))
    : state.currentTime;
  const ratio = playedRatio(displayedTime, state.duration);
  const currentLabel = formatMediaTime(displayedTime);
  const durationLabel =
    state.duration > 0 ? formatMediaTime(state.duration) : "--:--";

  const fill = host.querySelector(
    ".attachment-preview-video-progress-fill",
  ) as HTMLElement | null;
  if (fill) fill.style.width = `${(ratio * 100).toFixed(2)}%`;

  const currentEl = host.querySelector(
    ".attachment-preview-video-progress-current",
  ) as HTMLElement | null;
  if (currentEl) currentEl.textContent = currentLabel;

  const durationEl = host.querySelector(
    ".attachment-preview-video-progress-duration",
  ) as HTMLElement | null;
  if (durationEl) durationEl.textContent = durationLabel;

  // Play / pause state — the native control strip is gone
  // (JAISIU-4447), so the custom button is the only affordance.
  const playBtn = host.querySelector(
    ".attachment-preview-video-progress-play",
  ) as HTMLElement | null;
  if (playBtn) {
    const paused = media.paused !== false;
    playBtn.textContent = paused ? "▶" : "❚❚";
    playBtn.setAttribute("aria-label", paused ? "Play" : "Pause");
    playBtn.setAttribute("title", paused ? "Play" : "Pause");
    playBtn.setAttribute("data-paused", paused ? "1" : "0");
  }

  // Volume slider — mirror the media element's volume, but never
  // fight the operator while they drag the slider.
  const volumeEl = host.querySelector(
    ".attachment-preview-video-progress-volume",
  ) as HTMLInputElement | null;
  if (volumeEl && typeof volumeEl.value !== "undefined") {
    const next = normalizeVolume(media.volume);
    if (Number(volumeEl.value) !== next) {
      volumeEl.value = String(next);
    }
  }

  track.setAttribute("aria-valuemax", String(state.duration));
  track.setAttribute("aria-valuenow", String(displayedTime));
  track.setAttribute("aria-valuetext", `${currentLabel} of ${durationLabel}`);
  track.setAttribute("data-duration", String(state.duration));
  track.setAttribute("data-current-time", String(displayedTime));
  track.setAttribute("data-ratio", String(ratio));
  return true;
}
